/** Tab strip model for the aux-sync review page.
 *
 *  The strip answers one question: where is there work to do? So "empty"
 *  here means **this tab offers no changes** — not "this tab has no rows":
 *
 *    - a kind whose records are all `existing` is already configured, and
 *      approving it would write nothing;
 *    - an `info` kind is reference data (available languages, note-section
 *      keys, product options) that can never be applied at all;
 *    - a kind with no records is trivially in the same boat.
 *
 *  All three sort AFTER every tab with pending changes and render greyed.
 *  They stay visible and clickable: an addable kind is where a manual
 *  record gets added, an all-existing kind is how you confirm what is
 *  already configured, and info kinds are worth browsing.
 */
import type { AuxKindSpec } from '@/api/auxSync';

export const SECTION_ORDER = ['rosters', 'config', 'mapping', 'info'] as const;

export const SECTION_LABEL: Record<string, string> = {
  rosters: 'Rosters',
  config: 'Config',
  mapping: 'Mapping rules',
  info: 'Info',
};

type TabKind = Pick<AuxKindSpec, 'count' | 'section' | 'label' | 'target' | 'counts_by_status'>;

/** How many records on this tab are worth acting on: new + changed, and
 *  zero for read-only `info` kinds whichever status their rows carry
 *  (the differ marks them `new`, but nothing about them is applicable).
 *
 *  Deliberately derived from the artifact totals, NOT the active status
 *  filter — toggling the status chips must never reshuffle the strip
 *  under the user's cursor. */
export function pendingChanges(kind: TabKind): number {
  if (kind.target === 'info') return 0;
  const by = kind.counts_by_status ?? {};
  return (by.new ?? 0) + (by.changed ?? 0);
}

export function offersChanges(kind: TabKind): boolean {
  return pendingChanges(kind) > 0;
}

/** Which kinds get a tab at all: anything holding records, plus empty
 *  kinds the user can still add records to. */
export function visibleKindTabs<T extends Pick<AuxKindSpec, 'count' | 'addable'>>(
  kinds: readonly T[],
): T[] {
  return kinds.filter((k) => (k.count ?? 0) > 0 || k.addable);
}

/** Tabs offering changes first, then the section/label order the strip has
 *  always used. */
export function orderKindTabs<T extends TabKind>(kinds: readonly T[]): T[] {
  return [...kinds].sort(
    (a, b) =>
      Number(offersChanges(b)) - Number(offersChanges(a)) ||
      SECTION_ORDER.indexOf(a.section as (typeof SECTION_ORDER)[number]) -
        SECTION_ORDER.indexOf(b.section as (typeof SECTION_ORDER)[number]) ||
      a.label.localeCompare(b.label),
  );
}

/** Why a greyed tab is greyed — the native tooltip on it. */
export function noChangesReason(kind: TabKind): string {
  if (kind.target === 'info') return 'Reference only — nothing to apply';
  if ((kind.count ?? 0) === 0) return 'No records found';
  return `Nothing to review — ${kind.count} record(s) already configured`;
}
